/** Render helpers shared by the strip, the stage and the folds. */

import type { DefsDirection } from '../types';

export const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The one check affordance — the board completes, never composes or edits.
 * `state` carries the optimistic posture: pending (write in flight) and
 * failed (the write bounced and we reverted).
 */
export function checkbox(
  kind: string,
  id: string,
  done: boolean,
  state: { pending: boolean; failed: boolean },
): string {
  const cls = ['ck', done ? 'on' : '', state.pending ? 'pending' : '', state.failed ? 'failed' : '']
    .filter(Boolean)
    .join(' ');
  const label = state.failed ? "didn't take — tap again" : done ? 'uncheck' : 'check';
  return `<button class="${cls}" data-check="${kind}" data-id="${esc(id)}" data-done="${done ? '1' : '0'}" title="${label}" aria-label="${label}" aria-pressed="${done}">${done ? '✓' : ''}</button>`;
}

/**
 * Direction imagery. Real bytes are the defs-pipeline / foundation tickets'
 * job (defs/images/README.md); until an image exists, a stable per-Direction
 * gradient stands in — placeholders that still read as identity, like the
 * prototype's.
 */
const PLACEHOLDERS: Record<string, string> = {
  'meta-learning': 'linear-gradient(160deg,#2b3a67,#4c3fbe)',
  'startup-series-b': 'linear-gradient(160deg,#0f3a4a,#1776aa)',
  family: 'linear-gradient(160deg,#5a3a2e,#a05a3c)',
  'food-fit': 'linear-gradient(160deg,#1d4a33,#2e8b57)',
  agency: 'linear-gradient(160deg,#3d2b4f,#7b4fa0)',
};

const FALLBACKS = [
  'linear-gradient(160deg,#2b3a67,#4c3fbe)',
  'linear-gradient(160deg,#0f3a4a,#1776aa)',
  'linear-gradient(160deg,#5a3a2e,#a05a3c)',
  'linear-gradient(160deg,#1d4a33,#2e8b57)',
  'linear-gradient(160deg,#3d2b4f,#7b4fa0)',
  'linear-gradient(160deg,#4a2f3a,#a03c5a)',
];

export function directionImage(d: DefsDirection): string {
  if (d.image) return `url("/defs-images/${encodeURI(d.image.replace(/^images\//, ''))}")`;
  if (PLACEHOLDERS[d.id]) return PLACEHOLDERS[d.id];
  let h = 0;
  for (const ch of d.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return FALLBACKS[h % FALLBACKS.length];
}

/** Local-date arithmetic on YYYY-MM-DD strings (the Worker owns the timezone). */
export function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return NaN;
  return Math.round((b - a) / 86_400_000);
}

export function shiftDate(iso: string, days: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

const DAY_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Weekday letters for the trailing 7 local days ending today. */
export function weekHeader(today: string): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(Date.parse(`${shiftDate(today, i - 6)}T00:00:00Z`));
    return DAY_LETTER[d.getUTCDay()];
  });
}

export function hhmm(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function elapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
